import React, { useState, useEffect } from 'react';
import {
  Modal,
  Card,
  Row,
  Col,
  Statistic,
  Radio,
  InputNumber,
  Form,
  Input,
  Space,
  Alert,
  Divider,
  Progress,
} from 'antd';
import {
  UserOutlined,
  DatabaseOutlined,
  DollarOutlined,
} from '@ant-design/icons';
import { DesignTokens, getCapacityColor } from './DesignTokens';

const { TextArea } = Input;

export interface CurrentCapacity {
  userLimit: number;
  usedUsers: number;
  storageLimit: number;
  usedStorage: number;
}

export interface CapacityConfig {
  userLimit: number;
  storageLimit: number;
  remark?: string;
}

export interface CapacityPlan {
  id: string;
  label: string;
  userLimit: number;
  storageLimit: number;
  monthlyFee: number;
  description: string;
}

export interface CapacityModalProps {
  open: boolean;
  currentCapacity: CurrentCapacity;
  onOk?: (config: CapacityConfig) => void;
  onCancel?: () => void;
  loading?: boolean;
}

const CAPACITY_PLANS: CapacityPlan[] = [
  {
    id: 'small',
    label: '小规模扩容',
    userLimit: 150,
    storageLimit: 100,
    monthlyFee: 500,
    description: '适合小型团队,+50用户/+50GB存储',
  },
  {
    id: 'medium',
    label: '中规模扩容',
    userLimit: 300,
    storageLimit: 200,
    monthlyFee: 1000,
    description: '适合中型企业,+200用户/+150GB存储',
  },
  {
    id: 'large',
    label: '大规模扩容',
    userLimit: 500,
    storageLimit: 500,
    monthlyFee: 2000,
    description: '适合大型企业,+400用户/+450GB存储',
  },
  {
    id: 'custom',
    label: '自定义配置',
    userLimit: 0,
    storageLimit: 0,
    monthlyFee: 0,
    description: '根据实际需求自定义容量',
  },
];

export const CapacityModal: React.FC<CapacityModalProps> = ({
  open,
  currentCapacity,
  onOk,
  onCancel,
  loading = false,
}) => {
  const [form] = Form.useForm();
  const [selectedPlan, setSelectedPlan] = useState<string>('small');
  const [customConfig, setCustomConfig] = useState({
    userLimit: currentCapacity.userLimit,
    storageLimit: currentCapacity.storageLimit,
  });

  useEffect(() => {
    if (open) {
      form.resetFields();
      setSelectedPlan('small');
      setCustomConfig({
        userLimit: currentCapacity.userLimit,
        storageLimit: currentCapacity.storageLimit,
      });
    }
  }, [open, currentCapacity, form]);

  const handlePlanChange = (planId: string) => {
    setSelectedPlan(planId);
    if (planId !== 'custom') {
      const plan = CAPACITY_PLANS.find((p) => p.id === planId);
      if (plan) {
        form.setFieldsValue({
          userLimit: plan.userLimit,
          storageLimit: plan.storageLimit,
        });
      }
    }
  };

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      onOk?.(values);
    } catch (error) {
      console.error('表单验证失败:', error);
    }
  };

  const currentPlan = CAPACITY_PLANS.find((p) => p.id === selectedPlan);
  const isCustom = selectedPlan === 'custom';

  const newUserLimit = isCustom ? customConfig.userLimit : currentPlan?.userLimit || 0;
  const newStorageLimit = isCustom ? customConfig.storageLimit : currentPlan?.storageLimit || 0;

  const userUsagePercent = Math.round((currentCapacity.usedUsers / currentCapacity.userLimit) * 100);
  const storageUsagePercent = Math.round((currentCapacity.usedStorage / currentCapacity.storageLimit) * 100);

  const estimatedFee = isCustom
    ? Math.round(
        ((newUserLimit - currentCapacity.userLimit) * 5 +
          (newStorageLimit - currentCapacity.storageLimit) * 2) *
          1.2
      )
    : currentPlan?.monthlyFee || 0;

  return (
    <Modal
      title="容量管理 - 扩容配置"
      open={open}
      onOk={handleOk}
      onCancel={onCancel}
      confirmLoading={loading}
      width={800}
      okText="确认扩容"
      cancelText="取消"
      styles={{
        header: {
          background: DesignTokens.colors.gray2,
          padding: `${DesignTokens.spacing.lg}px ${DesignTokens.spacing.xl}px`,
        },
        body: {
          padding: DesignTokens.spacing.xl,
        },
      }}
    >
      {/* 当前容量概览 */}
      <Alert
        message="当前容量使用情况"
        description={
          <Row gutter={16} style={{ marginTop: DesignTokens.spacing.md }}>
            <Col span={12}>
              <Card size="small">
                <Statistic
                  title="用户数"
                  value={currentCapacity.usedUsers}
                  suffix={`/ ${currentCapacity.userLimit}`}
                  prefix={<UserOutlined />}
                />
                <Progress
                  percent={userUsagePercent}
                  strokeColor={getCapacityColor(userUsagePercent)}
                  size="small"
                  style={{ marginTop: 8 }}
                />
              </Card>
            </Col>
            <Col span={12}>
              <Card size="small">
                <Statistic
                  title="存储空间 (GB)"
                  value={currentCapacity.usedStorage}
                  suffix={`/ ${currentCapacity.storageLimit}`}
                  prefix={<DatabaseOutlined />}
                />
                <Progress
                  percent={storageUsagePercent}
                  strokeColor={getCapacityColor(storageUsagePercent)}
                  size="small"
                  style={{ marginTop: 8 }}
                />
              </Card>
            </Col>
          </Row>
        }
        type="info"
        style={{ marginBottom: DesignTokens.spacing.xl }}
      />

      <Form
        form={form}
        layout="vertical"
        initialValues={{
          userLimit: CAPACITY_PLANS[0].userLimit,
          storageLimit: CAPACITY_PLANS[0].storageLimit,
        }}
      >
        {/* 扩容方案选择 */}
        <Divider orientation="left" style={{ ...DesignTokens.typography.h3 }}>
          选择扩容方案
        </Divider>

        <Radio.Group
          value={selectedPlan}
          onChange={(e) => handlePlanChange(e.target.value)}
          style={{ width: '100%', marginBottom: DesignTokens.spacing.xl }}
        >
          <Space direction="vertical" style={{ width: '100%' }} size="middle">
            {CAPACITY_PLANS.map((plan) => (
              <Radio key={plan.id} value={plan.id} style={{ width: '100%' }}>
                <Card
                  size="small"
                  style={{
                    width: '100%',
                    borderColor:
                      selectedPlan === plan.id
                        ? DesignTokens.colors.primary
                        : DesignTokens.colors.gray4,
                    borderWidth: 2,
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <div style={{ fontWeight: 600, fontSize: 16, marginBottom: 4 }}>
                        {plan.label}
                      </div>
                      <div style={{ color: DesignTokens.colors.gray7, fontSize: 12 }}>
                        {plan.description}
                      </div>
                      {plan.id !== 'custom' && (
                        <div style={{ marginTop: 8 }}>
                          <Space split="|">
                            <span>
                              用户数: <strong>{plan.userLimit}</strong>
                            </span>
                            <span>
                              存储: <strong>{plan.storageLimit}GB</strong>
                            </span>
                          </Space>
                        </div>
                      )}
                    </div>
                    {plan.id !== 'custom' && (
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ fontSize: 24, fontWeight: 600, color: DesignTokens.colors.primary }}>
                          ¥{plan.monthlyFee}
                        </div>
                        <div style={{ fontSize: 12, color: DesignTokens.colors.gray7 }}>/ 月</div>
                      </div>
                    )}
                  </div>
                </Card>
              </Radio>
            ))}
          </Space>
        </Radio.Group>

        {/* 自定义配置 */}
        {isCustom && (
          <>
            <Divider orientation="left" style={{ ...DesignTokens.typography.h3 }}>
              自定义容量配置
            </Divider>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  label="用户数上限"
                  name="userLimit"
                  rules={[
                    { required: true, message: '请输入用户数上限' },
                    {
                      type: 'number',
                      min: currentCapacity.userLimit,
                      message: `不能小于当前配置(${currentCapacity.userLimit})`,
                    },
                  ]}
                >
                  <InputNumber
                    min={currentCapacity.userLimit}
                    max={10000}
                    addonAfter="人"
                    style={{ width: '100%' }}
                    onChange={(value) =>
                      setCustomConfig({ ...customConfig, userLimit: value || currentCapacity.userLimit })
                    }
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label="存储空间上限"
                  name="storageLimit"
                  rules={[
                    { required: true, message: '请输入存储空间上限' },
                    {
                      type: 'number',
                      min: currentCapacity.storageLimit,
                      message: `不能小于当前配置(${currentCapacity.storageLimit})`,
                    },
                  ]}
                >
                  <InputNumber
                    min={currentCapacity.storageLimit}
                    max={10000}
                    addonAfter="GB"
                    style={{ width: '100%' }}
                    onChange={(value) =>
                      setCustomConfig({ ...customConfig, storageLimit: value || currentCapacity.storageLimit })
                    }
                  />
                </Form.Item>
              </Col>
            </Row>
          </>
        )}

        {/* 费用预估 */}
        <Card
          style={{
            background: `linear-gradient(135deg, ${DesignTokens.colors.primaryLight} 0%, ${DesignTokens.colors.primary} 100%)`,
            marginBottom: DesignTokens.spacing.xl,
          }}
        >
          <Row align="middle">
            <Col flex="auto">
              <div style={{ color: DesignTokens.colors.gray1 }}>
                <DollarOutlined style={{ fontSize: 24, marginRight: 8 }} />
                <span style={{ fontSize: 16 }}>预估费用</span>
              </div>
              <div style={{ color: DesignTokens.colors.gray1, fontSize: 12, marginTop: 4, opacity: 0.9 }}>
                扩容后每月费用预估
              </div>
            </Col>
            <Col>
              <div style={{ fontSize: 32, fontWeight: 600, color: DesignTokens.colors.gray1 }}>
                ¥{estimatedFee}
              </div>
              <div style={{ fontSize: 12, color: DesignTokens.colors.gray1, opacity: 0.9 }}>/ 月</div>
            </Col>
          </Row>
        </Card>

        {/* 备注说明 */}
        <Form.Item label="备注说明" name="remark">
          <TextArea
            rows={3}
            placeholder="请输入扩容原因或备注信息..."
            maxLength={200}
            showCount
          />
        </Form.Item>
      </Form>

      {/* 温馨提示 */}
      <Alert
        message="温馨提示"
        description={
          <ul style={{ margin: 0, paddingLeft: 20 }}>
            <li>扩容将在确认后立即生效</li>
            <li>费用按月结算,扩容当月按实际使用天数计费</li>
            <li>如需缩减容量,请联系客服处理</li>
          </ul>
        }
        type="warning"
        showIcon
      />
    </Modal>
  );
};
